import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Sparkles, BookOpen, ExternalLink, Tag,
  Wand2, Feather, Pencil, ChevronRight, Star, Flame, Target,
} from 'lucide-react';

// ============================================================
// Research
// ============================================================
export function ResearchPanel({ api }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ title: '', content: '', tags: '', source_url: '' });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/book/research');
      setItems(data);
    } catch (e) { /* soft */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft.title.trim()) return toast.error('Title required');
    const payload = {
      title: draft.title,
      content: draft.content,
      tags: draft.tags.split(',').map(t => t.trim()).filter(Boolean),
      source_url: draft.source_url,
    };
    try {
      if (editingId) {
        await api.put(`/book/research/${editingId}`, payload);
        toast.success('Updated');
      } else {
        await api.post('/book/research', payload);
        toast.success('Saved');
      }
      setDraft({ title: '', content: '', tags: '', source_url: '' });
      setEditingId(null);
      load();
    } catch (e) { toast.error('Save failed'); }
  };

  const edit = (r) => {
    setDraft({
      title: r.title,
      content: r.content || '',
      tags: (r.tags || []).join(', '),
      source_url: r.source_url || '',
    });
    setEditingId(r.id);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.delete(`/book/research/${id}`);
      toast.success('Deleted');
      if (editingId === id) {
        setEditingId(null);
        setDraft({ title: '', content: '', tags: '', source_url: '' });
      }
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-6" data-testid="research-panel">
      <div className="space-y-3">
        <h2 className="book-display text-2xl">Research & Quotes</h2>
        <p className="text-[var(--ink-soft)] italic text-sm">Source material, quotes, stories you want to weave in. Tag them so you can find them later.</p>
        {items.length === 0 && (
          <div className="sheet p-8 text-center text-[var(--ink-fade)] italic">No notes yet.</div>
        )}
        {items.map(r => (
          <div key={r.id} className="sheet p-4" data-testid={`research-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <h3 className="book-display text-lg font-semibold flex-1">{r.title}</h3>
              <button onClick={() => edit(r)} className="btn-ghost !p-1.5"><Pencil className="h-3 w-3" /></button>
              <button onClick={() => remove(r.id)} className="btn-ghost !p-1.5 text-[var(--rust)]"><Trash2 className="h-3 w-3" /></button>
            </div>
            {r.source_url && (
              <a href={r.source_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--accent)] flex items-center gap-1 mt-1">
                <ExternalLink className="h-3 w-3" /> {r.source_url}
              </a>
            )}
            <p className="text-[var(--ink-soft)] mt-2 whitespace-pre-wrap text-[15px] leading-relaxed">{r.content}</p>
            {r.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {r.tags.map(t => (
                  <span key={t} className="chip chip-draft inline-flex items-center gap-1"><Tag className="h-2.5 w-2.5" />{t}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <aside className="sheet p-4 space-y-3 h-fit sticky top-4" data-testid="research-form">
        <h3 className="small-caps">{editingId ? 'Edit Note' : 'New Note'}</h3>
        <input className="book-input" placeholder="Title" value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} data-testid="research-title" />
        <textarea className="book-input" rows={8} placeholder="Quote, observation, raw idea…" value={draft.content} onChange={e => setDraft({ ...draft, content: e.target.value })} data-testid="research-content" />
        <input className="book-input" placeholder="Tags (comma-separated)" value={draft.tags} onChange={e => setDraft({ ...draft, tags: e.target.value })} />
        <input className="book-input" placeholder="Source URL (optional)" value={draft.source_url} onChange={e => setDraft({ ...draft, source_url: e.target.value })} />
        <div className="flex gap-2">
          <button onClick={save} className="btn-ink flex-1" data-testid="save-research">{editingId ? 'Update' : 'Save'}</button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setDraft({ title: '', content: '', tags: '', source_url: '' }); }} className="btn-ghost">Cancel</button>
          )}
        </div>
      </aside>
    </div>
  );
}

// ============================================================
// Characters / People profiles
// ============================================================
export function CharactersPanel({ api }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ name: '', role: '', description: '', notes: '' });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/book/characters'); setItems(data); } catch (e) {}
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft.name.trim()) return toast.error('Name required');
    try {
      if (editingId) await api.put(`/book/characters/${editingId}`, draft);
      else await api.post('/book/characters', draft);
      toast.success('Saved');
      setDraft({ name: '', role: '', description: '', notes: '' });
      setEditingId(null);
      load();
    } catch (e) { toast.error('Save failed'); }
  };

  const edit = (c) => {
    setDraft({ name: c.name, role: c.role || '', description: c.description || '', notes: c.notes || '' });
    setEditingId(c.id);
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this profile?')) return;
    try { await api.delete(`/book/characters/${id}`); toast.success('Deleted'); load(); } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="characters-panel">
      <div>
        <h2 className="book-display text-2xl">People & Characters</h2>
        <p className="text-[var(--ink-soft)] italic text-sm">Real or composite — the people whose stories shape yours.</p>
      </div>

      <div className="sheet p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="character-form">
        <input className="book-input" placeholder="Name" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} data-testid="char-name" />
        <input className="book-input" placeholder="Role (mentor, rival, family…)" value={draft.role} onChange={e => setDraft({ ...draft, role: e.target.value })} />
        <textarea className="book-input md:col-span-2" rows={2} placeholder="One-line description" value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} />
        <textarea className="book-input md:col-span-2" rows={4} placeholder="Notes, quotes, key moments…" value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} />
        <div className="md:col-span-2 flex gap-2">
          <button onClick={save} className="btn-ink" data-testid="save-character">{editingId ? 'Update' : 'Add'}</button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setDraft({ name: '', role: '', description: '', notes: '' }); }} className="btn-ghost">Cancel</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.length === 0 && (
          <div className="md:col-span-2 sheet p-8 text-center text-[var(--ink-fade)] italic">No people yet.</div>
        )}
        {items.map(c => (
          <div key={c.id} className="sheet p-4" data-testid={`character-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="book-display text-lg font-semibold">{c.name}</h3>
                {c.role && <p className="text-xs italic text-[var(--ink-fade)]">{c.role}</p>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => edit(c)} className="btn-ghost !p-1.5"><Pencil className="h-3 w-3" /></button>
                <button onClick={() => remove(c.id)} className="btn-ghost !p-1.5 text-[var(--rust)]"><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
            {c.description && <p className="text-[var(--ink-soft)] mt-2 text-[15px] italic">{c.description}</p>}
            {c.notes && <p className="text-[var(--ink-soft)] mt-2 text-sm whitespace-pre-wrap">{c.notes}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Beats / timeline
// ============================================================
export function BeatsPanel({ api }) {
  const [items, setItems] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [draft, setDraft] = useState({ title: '', description: '', chapter_id: '', when: '' });

  const load = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([api.get('/book/beats'), api.get('/book/chapters')]);
      setItems(b.data);
      setChapters(c.data);
    } catch (e) {}
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.title.trim()) return toast.error('Title required');
    try {
      await api.post('/book/beats', draft);
      setDraft({ title: '', description: '', chapter_id: '', when: '' });
      load();
    } catch (e) { toast.error('Save failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this beat?')) return;
    try { await api.delete(`/book/beats/${id}`); load(); } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="beats-panel">
      <div>
        <h2 className="book-display text-2xl">Story Beats & Timeline</h2>
        <p className="text-[var(--ink-soft)] italic text-sm">Key moments in order — the spine of the story.</p>
      </div>

      <div className="sheet p-4 grid grid-cols-1 md:grid-cols-12 gap-2" data-testid="beat-form">
        <input className="book-input md:col-span-4" placeholder="Beat title" value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} data-testid="beat-title" />
        <input className="book-input md:col-span-2" placeholder="When (Age 22, 2018…)" value={draft.when} onChange={e => setDraft({ ...draft, when: e.target.value })} />
        <select className="book-input md:col-span-3 ui-font !text-sm" value={draft.chapter_id} onChange={e => setDraft({ ...draft, chapter_id: e.target.value })}>
          <option value="">— No chapter yet —</option>
          {chapters.map(c => (<option key={c.id} value={c.id}>{c.title}</option>))}
        </select>
        <input className="book-input md:col-span-3" placeholder="One-line description" value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} />
        <button onClick={add} className="btn-ink md:col-span-12" data-testid="add-beat"><Plus className="h-4 w-4 inline mr-1" /> Add beat</button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="sheet p-8 text-center text-[var(--ink-fade)] italic">No beats yet.</div>
        )}
        {items.map((b, i) => {
          const chap = chapters.find(c => c.id === b.chapter_id);
          return (
            <div key={b.id} className="sheet p-3 flex items-start gap-3" data-testid={`beat-${b.id}`}>
              <div className="small-caps mt-1 w-8 text-right">{String(i + 1).padStart(2, '0')}</div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="book-display font-semibold">{b.title}</h4>
                  {b.when && <span className="text-xs italic text-[var(--ink-fade)]">· {b.when}</span>}
                  {chap && <span className="chip chip-progress">{chap.title}</span>}
                </div>
                {b.description && <p className="text-[var(--ink-soft)] text-sm mt-1">{b.description}</p>}
              </div>
              <button onClick={() => remove(b.id)} className="btn-ghost !p-1.5 text-[var(--rust)]"><Trash2 className="h-3 w-3" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// Stats — streak, history, totals
// ============================================================
export function StatsPanel({ stats, onRefresh }) {
  useEffect(() => { onRefresh?.(); /* eslint-disable-next-line */ }, []);

  if (!stats) return <div className="sheet p-8 text-center text-[var(--ink-fade)]">Loading…</div>;

  const maxBar = Math.max(...stats.history.map(h => h.words), 1);
  const today = stats.history[stats.history.length - 1]?.date;

  return (
    <div className="space-y-6" data-testid="stats-panel">
      <h2 className="book-display text-2xl">Your Writing Life</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Streak" value={`${stats.streak}d`} icon={<Flame className="h-5 w-5 text-[var(--rust)]" />} />
        <StatCard label="Today" value={`${stats.today_words}w`} sub={`/ ${stats.daily_target} goal`} icon={<Target className="h-5 w-5 text-[var(--accent)]" />} />
        <StatCard label="Total words" value={stats.total_words.toLocaleString()} sub={`/ ${stats.total_target.toLocaleString()}`} icon={<BookOpen className="h-5 w-5 text-[var(--ink)]" />} />
        <StatCard label="Chapters" value={`${stats.chapters_done}/${stats.chapters}`} sub="done / total" icon={<Star className="h-5 w-5 text-[var(--gold)]" />} />
      </div>

      <div className="sheet p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="small-caps">Last 14 days</h3>
          <span className="text-xs text-[var(--ink-fade)] italic">Today is highlighted</span>
        </div>
        <div className="flex items-end gap-1.5 h-40" data-testid="sparkline">
          {stats.history.map(h => {
            const isToday = h.date === today;
            const height = h.words === 0 ? 4 : Math.max(8, (h.words / maxBar) * 140);
            return (
              <div key={h.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${h.date}: ${h.words} words`}>
                <div className={`spark-bar w-full ${h.words > 0 ? 'has' : ''} ${isToday ? 'today' : ''}`} style={{ height: `${height}px` }} />
                <div className="text-[9px] text-[var(--ink-fade)]">{h.date.slice(8)}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="sheet p-5">
        <h3 className="small-caps mb-2">Progress to goal</h3>
        <div className="progress mb-2"><div style={{ width: `${Math.min(stats.total_pct, 100)}%` }} /></div>
        <div className="flex justify-between text-sm">
          <span className="italic text-[var(--ink-soft)]">{stats.total_words.toLocaleString()} of {stats.total_target.toLocaleString()} words</span>
          <span className="font-semibold ui-font text-[var(--accent)]">{stats.total_pct.toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon }) {
  return (
    <div className="sheet p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="small-caps">{label}</span>
        {icon}
      </div>
      <div className="book-display text-3xl font-semibold">{value}</div>
      {sub && <div className="text-xs italic text-[var(--ink-fade)] mt-1">{sub}</div>}
    </div>
  );
}

// ============================================================
// Settings — meta, goals
// ============================================================
export function SettingsPanel({ api, meta, onSaved }) {
  const [draft, setDraft] = useState(meta || {});
  useEffect(() => { setDraft(meta || {}); }, [meta]);

  const save = async () => {
    try {
      await api.put('/book/meta', {
        title: draft.title || 'Untitled',
        subtitle: draft.subtitle || '',
        author: draft.author || '',
        tagline: draft.tagline || '',
        daily_word_target: parseInt(draft.daily_word_target) || 500,
        total_word_target: parseInt(draft.total_word_target) || 60000,
        blurb: draft.blurb || '',
      });
      toast.success('Saved');
      onSaved?.();
    } catch (e) { toast.error('Save failed'); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="settings-panel">
      <h2 className="book-display text-2xl">Book setup</h2>
      <div className="sheet p-5 space-y-3">
        <Field label="Title">
          <input className="book-input !text-xl" value={draft.title || ''} onChange={e => setDraft({ ...draft, title: e.target.value })} data-testid="meta-title" />
        </Field>
        <Field label="Subtitle">
          <input className="book-input italic" value={draft.subtitle || ''} onChange={e => setDraft({ ...draft, subtitle: e.target.value })} />
        </Field>
        <Field label="Author">
          <input className="book-input" value={draft.author || ''} onChange={e => setDraft({ ...draft, author: e.target.value })} />
        </Field>
        <Field label="Tagline / pitch">
          <input className="book-input" value={draft.tagline || ''} onChange={e => setDraft({ ...draft, tagline: e.target.value })} />
        </Field>
        <Field label="Blurb (back-cover description)">
          <textarea rows={4} className="book-input" value={draft.blurb || ''} onChange={e => setDraft({ ...draft, blurb: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Daily word goal">
            <input type="number" min="0" className="book-input" value={draft.daily_word_target || 500} onChange={e => setDraft({ ...draft, daily_word_target: e.target.value })} data-testid="meta-daily-target" />
          </Field>
          <Field label="Total word target">
            <input type="number" min="0" className="book-input" value={draft.total_word_target || 60000} onChange={e => setDraft({ ...draft, total_word_target: e.target.value })} />
          </Field>
        </div>
        <button onClick={save} className="btn-ink" data-testid="save-meta">Save</button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="small-caps block mb-1">{label}</label>
      {children}
    </div>
  );
}

// ============================================================
// AI Co-writer — brainstorm / polish / continue / outline
// ============================================================
export function AiCowriter({ api, currentText, onInsert, onReplace }) {
  const [mode, setMode] = useState('continue');
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('clear and engaging');
  const [length, setLength] = useState('one paragraph');
  const [count, setCount] = useState(5);
  const [chapters, setChapters] = useState(12);
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    setOutput('');
    try {
      let resp;
      if (mode === 'brainstorm') {
        resp = await api.post('/book/ai/brainstorm', { prompt, count: parseInt(count) });
        setOutput(resp.data.ideas);
      } else if (mode === 'polish') {
        const text = prompt || currentText;
        if (!text?.trim()) { toast.error('Need text to polish — paste or write some first'); setLoading(false); return; }
        resp = await api.post('/book/ai/polish', { text, tone });
        setOutput(resp.data.polished);
      } else if (mode === 'continue') {
        const text = currentText || prompt;
        if (!text?.trim()) { toast.error('Need existing text to continue from'); setLoading(false); return; }
        resp = await api.post('/book/ai/continue', { text, direction: prompt, length });
        setOutput(resp.data.continuation);
      } else if (mode === 'outline') {
        if (!prompt.trim()) { toast.error('Premise required'); setLoading(false); return; }
        resp = await api.post('/book/ai/outline', { premise: prompt, chapters: parseInt(chapters) });
        setOutput(resp.data.outline);
      } else if (mode === 'proofread') {
        const text = prompt || currentText;
        if (!text?.trim()) { toast.error('Need text to proofread — write some first'); setLoading(false); return; }
        resp = await api.post('/book/ai/proofread', { text });
        setOutput(resp.data.proofread);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI failed');
    }
    setLoading(false);
  };

  return (
    <div className="ai-panel p-4 space-y-3" data-testid="ai-cowriter">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="small-caps flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[var(--gold)]" /> AI Co-writer</h3>
        <div className="flex gap-1 flex-wrap" data-testid="ai-modes">
          {['continue', 'polish', 'proofread', 'brainstorm', 'outline'].map(m => (
            <button
              key={m}
              onClick={() => { setMode(m); setOutput(''); }}
              className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded border transition-all ${mode === m ? 'bg-[var(--accent)] text-white border-[var(--accent)]' : 'bg-white text-[var(--ink-soft)] border-[var(--rule)] hover:border-[var(--accent)]'}`}
              data-testid={`ai-mode-${m}`}>
              {m}
            </button>
          ))}
        </div>
      </div>

      {mode === 'continue' && (
        <>
          <p className="text-xs italic text-[var(--ink-soft)]">Will pick up where your current chapter ends. Optionally tell it which direction to take.</p>
          <div className="flex gap-2">
            <input className="book-input flex-1" placeholder="Optional: 'go deeper on the doubt I felt'" value={prompt} onChange={e => setPrompt(e.target.value)} data-testid="ai-prompt" />
            <select className="book-input !w-auto ui-font !text-sm" value={length} onChange={e => setLength(e.target.value)}>
              <option value="one short paragraph">Short ¶</option>
              <option value="one paragraph">One ¶</option>
              <option value="two paragraphs">Two ¶</option>
              <option value="a full scene">Full scene</option>
            </select>
          </div>
        </>
      )}

      {mode === 'polish' && (
        <>
          <p className="text-xs italic text-[var(--ink-soft)]">Rewrites your current chapter — or paste a specific passage below.</p>
          <textarea className="book-input" rows={3} placeholder="Leave blank to polish the whole chapter, or paste a passage…" value={prompt} onChange={e => setPrompt(e.target.value)} data-testid="ai-prompt" />
          <input className="book-input" placeholder="Tone (e.g. 'punchy and direct')" value={tone} onChange={e => setTone(e.target.value)} />
        </>
      )}

      {mode === 'proofread' && (
        <>
          <p className="text-xs italic text-[var(--ink-soft)]">Fixes punctuation, capitalization, spacing, grammar and paragraph breaks — without changing your voice. Use <strong>Replace chapter</strong> to apply the cleaned-up version.</p>
          <textarea className="book-input" rows={3} placeholder="Leave blank to proofread the whole chapter, or paste a passage…" value={prompt} onChange={e => setPrompt(e.target.value)} data-testid="ai-prompt" />
        </>
      )}

      {mode === 'brainstorm' && (
        <>
          <p className="text-xs italic text-[var(--ink-soft)]">Idea generator. Give it a seed — get a numbered list of angles.</p>
          <div className="flex gap-2">
            <input className="book-input flex-1" placeholder="Brainstorm chapters about scaling from 1 to 50 staff…" value={prompt} onChange={e => setPrompt(e.target.value)} data-testid="ai-prompt" />
            <input type="number" min="3" max="10" className="book-input !w-20" value={count} onChange={e => setCount(e.target.value)} />
          </div>
        </>
      )}

      {mode === 'outline' && (
        <>
          <p className="text-xs italic text-[var(--ink-soft)]">Drafts a full chapter outline from a one-line premise.</p>
          <div className="flex gap-2">
            <input className="book-input flex-1" placeholder="A young CEO learns leadership the hard way…" value={prompt} onChange={e => setPrompt(e.target.value)} data-testid="ai-prompt" />
            <input type="number" min="5" max="24" className="book-input !w-20" value={chapters} onChange={e => setChapters(e.target.value)} />
          </div>
        </>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={run} disabled={loading} className="btn-accent flex items-center gap-1" data-testid="ai-run">
          <Wand2 className="h-3.5 w-3.5" /> {loading ? 'Thinking…' : 'Generate'}
        </button>
        {output && mode === 'proofread' && (
          <button
            onClick={() => {
              if (!window.confirm('Replace the chapter content with the proofread version? Your current text will be overwritten (autosave will keep the new version).')) return;
              onReplace?.(output);
              toast.success('Chapter replaced with proofread version');
            }}
            className="btn-ink"
            data-testid="ai-replace">
            Replace chapter ⇄
          </button>
        )}
        {output && mode !== 'proofread' && (
          <button onClick={() => { onInsert?.(output); toast.success('Inserted at end of chapter'); }} className="btn-ink" data-testid="ai-insert">
            Insert into chapter ↓
          </button>
        )}
        {output && mode === 'polish' && (
          <button
            onClick={() => {
              if (!window.confirm('Replace the chapter content with the polished version?')) return;
              onReplace?.(output);
              toast.success('Chapter replaced with polished version');
            }}
            className="btn-ghost"
            data-testid="ai-replace-polish">
            Or replace chapter ⇄
          </button>
        )}
      </div>

      {output && (
        <div className="ai-output" data-testid="ai-output">{output}</div>
      )}
    </div>
  );
}

// Exported but currently unused in BookStudio root; reserved for future
// chapter-tree drag-reorder UI.
// eslint-disable-next-line no-unused-vars
export function ChaptersPanel() { return null; }
